<template>
  <v-dialog
    v-model="showModal"
    persistent
    max-width="900px"
    @keydown.enter.prevent="submit()"
    @keydown.esc.prevent="cancel()"
  >
    <el-overlay-loading :loading="pageloading" />
    <v-card class="modalCard">
      <v-card-title class="modalTitle pt-2 pb-1 mb-2">
        <h2>{{ sarasasTitle }}</h2>
      </v-card-title>
      <v-card-text class="pt-2 pb-2">
        <v-card class="mb-2 pb-4">
          <v-card-text>
            <v-row align="baseline" class="mb-0">
              <template v-if="savoTurtas">
                <v-col cols="12" class="mb-2">
                  {{ $t('Pasirinkite objektus, kurių duomenis norite įkelti') }}
                </v-col>
              </template>

              <template v-if="showFail">
                <v-col cols="12">
                  <el-savitarna-alert dense outlined type="error">
                    {{ failMsg }}
                  </el-savitarna-alert>
                </v-col>
              </template>

              <template v-if="!savoTurtas">
                <v-col cols="12" md="8" v-if="isAdresas">
                  <el-adresas
                    v-model="adresasSelect"
                    return-object
                    :label="$t('Adresas')"
                    :tooltip="`${ntrTooltipPrefix} Turtas. Adresas. Paieškai`"
                    :counter="1000"
                    autofocus
                  />
                </v-col>
                <v-col cols="12" md="8" v-if="isRegistras">
                  <el-text-field
                    v-model="registrasSelect"
                    :placeholder="regNrPlaceholder"
                    return-object
                    :label="$t('Registras')"
                    :tooltip="`${ntrTooltipPrefix} Turtas. Registras. Paieškai`"
                    autofocus
                  />
                </v-col>

                <v-col cols="12" md="4" class="pb-2">
                  <el-button
                    primary
                    @click.native="getData()"
                    :loading="loading"
                    :disabled="isSearching || !canSearch"
                    >{{ $t('Ieškoti') }}</el-button
                  >
                  <el-button
                    @click.native="cancelSearch()"
                    :loading="loading"
                    :disabled="isSearching"
                    >{{ $t('Išvalyti') }}</el-button
                  >
                </v-col>
              </template>
            </v-row>

            <v-data-table
              :headers="filteredHeaders"
              :items="itemsList"
              :item-class="rowClass"
              :loading="tableLoading"
              :search="search"
              :custom-filter="customSearch"
              :footer-props="{
                showFirstLastPage: true,
                showCurrentPage: true,
                itemsPerPageText: this.$t('Rodyti po:'),
                itemsPerPageAllText: this.$t('visi'),
                itemsPerPageOptions: itemsPerPageOptions,
              }"
              :hide-default-footer="hideFooter"
              :items-per-page="pageSize"
              item-key="tur_identifikatorius"
            >
              <template #top>
                <v-toolbar flat>
                  <v-text-field
                    v-model="search"
                    prepend-icon="mdi-magnify"
                    label=""
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
              <template #[`header.actions`]="{ header }">
                {{ header.text }}
                <el-checkbox
                  v-if="showHeaderCheck"
                  class="align-center justify-center mt-0"
                  :validate-key="`check-all`"
                  v-model="headerCheck"
                  @click.native.stop="checkAll()"
                />
              </template>
              <template #[`item.rusis`]="{ item }">
                <v-row dense no-gutters>
                  <v-col cols="12" md="12">
                    {{ item.tur_ntr_rusis }}
                  </v-col>
                  <v-col cols="12" md="12">
                    <span class="text-gray">{{ item.tur_ntr_adresas }}</span>
                  </v-col>
                </v-row>
              </template>
              <template #[`item.actions`]="{ item }">
                <div class="text-center mb-2">
                  <el-checkbox
                    v-if="item.canCheck"
                    :validate-key="`check-${item.tur_identifikatorius}`"
                    :disabled="item.disabled"
                    v-model="pasirinktiTurtai"
                    :checkValue="item.tur_identifikatorius"
                    :key="`check-${item.tur_identifikatorius}`"
                    @click.native.stop="checkItem(item)"
                  />
                </div>
              </template>
              <template slot="loading">
                {{ $t('Kraunasi...') }}
              </template>
              <template slot="no-results">
                {{ $t('Duomenų nėra') }}
              </template>
              <template slot="no-data">
                {{ $t('Duomenų nėra') }}
              </template>
              <template #[`footer.page-text`]="props">
                {{ $t('Rodoma') }} {{ props.pageStart }}-{{ props.pageStop }}
                {{ $t('iš') }}
                {{ props.itemsLength }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions class="modalActions" elevation="5">
        <v-spacer></v-spacer>
        <el-button @click.native="cancelData()" :loading="loading">
          {{ $t('Uždaryti') }}
        </el-button>
        <el-button
          primary
          :disabled="!enableSubmit"
          :loading="loading"
          @click.native.stop="saveData()"
        >
          {{ $t('Įkelti') }}
        </el-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import busenaNtr from '@/mixins/busenaNtr'
import rowClass from '@/mixins/rowClass'
import tooltipNtr from '@/mixins/tooltipNtr'
import { regNrMaskFilter } from '@/mixins/regNrMask'

export default {
  name: 'ModalNtrTurtasSarasas',
  props: {
    value: Boolean,
    isAdresas: Boolean,
    isRegistras: Boolean,
    turtasTeise: Boolean,
  },
  components: {},
  mixins: [busenaNtr, rowClass, tooltipNtr],
  data() {
    return {
      pageloading: false,
      loading: false,
      tableLoading: false,
      adresasSelect: null,
      registrasSelect: null,
      showFail: false,
      failMsg: null,
      search: null,
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      isSearching: false,
      turtai: [],
      pasirinktiTurtai: [],
      headerCheck: false,
      regNrPlaceholder: '##/#########',
      regNrMask: regNrMaskFilter,
    }
  },
  computed: {
    ...mapGetters(['prasymasID']),
    showModal: {
      get() {
        return this.value
      },
      set(value) {
        this.adresasSelect = null
        this.registrasSelect = null
        this.showFail = false
        this.resetSearch()
        this.$emit('input', value)
      },
    },
    canSearch() {
      return this.adresasSelect || this.registrasSelect || this.savoTurtas
    },

    headers() {
      return [
        {
          text: this.$t('Pasirinkti'),
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 100,
        },
        {
          text: this.$t('Registras'),
          align: 'start',
          value: 'tur_registras',
          width: 150,
        },
        {
          text: this.$t('Unikalus numeris'),
          align: 'start',
          value: 'tur_identifikatorius',
          width: 170,
        },
        {
          text: this.$t('Turto rūšis'),
          align: 'start',
          value: 'rusis',
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    itemsList() {
      return this.turtai
        .map(item => {
          item.canCheck = item.enabled
          item.disabled = !item.enabled
          return item
        })
        .sort((a, b) => {
          if (!a.enabled && b.enabled) return 1
          return 0
        })
    },
    hideFooter() {
      return this.itemsList.length <= 10
    },
    enableSubmit() {
      return this.pasirinktiTurtai.length > 0
    },
    showHeaderCheck() {
      return !this.isPrasymasSeima
    },
    sarasasTitle() {
      if (this.isAdresas) {
        return this.$t('Nekilnojamojo turto paieška pagal adresą')
      }
      return this.$t('Įkelti nekilnojamąjį turtą')
    },
    savoTurtas() {
      return !this.isAdresas && !this.isRegistras
    },
  },
  watch: {
    showModal(val) {
      if (val) {
        if (this.savoTurtas) {
          this.getData()
        }
      }
    },
  },
  methods: {
    getData() {
      if (!this.canSearch) {
        return
      }
      this.loading = true
      this.tableLoading = true
      this.isSearching = true
      this.showFail = false
      this.resetSearch()

      const data = {
        code: this.isAdresas ? parseInt(this.adresasSelect.code) : null,
        reg_nr: this.isRegistras ? this.registrasSelect : null,
        savo_turtas: this.savoTurtas,
        pra_turtas_teise: this.turtasTeise,
      }

      this.$axios
        .post(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/paieska-sarasas`,
          data
        )
        .then(res => {
          this.turtai = res.data.turtai
        })
        .catch(err => {
          this.failMsg = err.response.data.message
          this.showFail = true
          window._VMA.$emit('DATA_ERROR', this.failMsg)
        })
        .finally(() => {
          this.tableLoading = false
          this.loading = false
          this.isSearching = false
        })
    },
    saveData() {
      this.loading = true

      const data = {
        tur_identifikatorius: this.pasirinktiTurtai,
        pra_turtas_teise: this.turtasTeise,
      }
      this.$axios
        .post(`ntrpaslaugos/prasymas/${this.prasymasID}/turtas/paieska`, data)
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.$emit('updateTurtasList')
          this.closeModal()
        })
        .catch(err => {
          this.$emit('updateTurtasList', err.response.data.message)
          this.closeModal()
          // window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    checkAll() {
      this.pasirinktiTurtai = []
      if (this.headerCheck) {
        this.turtai.forEach(item => {
          if (
            (!this.search || this.customSearch(null, this.search, item)) &&
            item.canCheck
          ) {
            this.pasirinktiTurtai.push(item.tur_identifikatorius)
          }
        })
      }
    },
    checkItem(turtas) {
      if (this.isPrasymasSeima) {
        const pasirinkta = this.pasirinktiTurtai.includes(
          turtas.tur_identifikatorius
        )
        this.turtai.forEach(item => {
          if (
            item.tur_identifikatorius !== turtas.tur_identifikatorius &&
            item.canCheck
          ) {
            item.disabled = pasirinkta
          }
        })
      }
    },
    cancelData() {
      this.closeModal()
    },
    closeModal() {
      this.showModal = false
    },
    submit() {
      this.getData()
    },
    cancel() {
      this.closeModal()
    },
    cancelSearch() {
      this.adresasSelect = null
      this.registrasSelect = null
      this.showFail = false
      this.resetSearch()
    },
    resetSearch() {
      this.headerCheck = false
      this.turtai = []
      this.pasirinktiTurtai = []
    },
    getActiveClass(item) {
      if (item.disabled) {
        return 'row-disabled'
      }
      return ''
    },
    customSearch(value, search, item) {
      return Object.values(item).some(
        v =>
          v &&
          v
            .toString()
            .toLowerCase()
            .includes(search.toLowerCase())
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.modalCard {
  background: #f5f5f5 !important;
}
.modalTitle {
  background: #f5f5f5 !important;
  position: sticky;
  top: 0;
  z-index: 2;
  background: none;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.modalActions {
  position: sticky;
  bottom: 0;
  z-index: 1;
}
</style>
